import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  BsArrowCounterclockwise,
  BsBoxSeam,
  BsChatLeftText,
  BsPlusLg,
} from "react-icons/bs";

import ModalSlideOver from "../cardmodal/ModalSlideOver";

import { serviceAddScaleParts } from "../../api/serviceApi";
import { useProducts } from "../../hooks/useProducts";

import "./jobsheetdrawerparts.css";
import { getImageUrl } from "../../utils/helper";

const getId = (item) => item?.id || item?._id || "";

const emptyPart = () => ({
  productId: "",
  quantity: 1,
  comment: "",
});

const getStatusClass = (status) => {
  return String(status || "required")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
};

const JobSheetDrawerParts = ({ open, jobId, parts = [], onClose, onSaved }) => {
  const { products, loadingProducts } = useProducts(open);
  const [displayParts, setDisplayParts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [partForm, setPartForm] = useState(emptyPart());

  // ==========================================================
  // SYNC JOB PARTS
  // ==========================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setPartForm(emptyPart());
  }, [open]);

  useEffect(() => {
    if (open) {
      setDisplayParts(Array.isArray(parts) ? parts : []);
    }
  }, [parts, open]);

  // ==========================================================
  // SELECTED PRODUCT
  // ==========================================================

  const selectedProduct = useMemo(() => {
    return products.find((product) => getId(product) === partForm.productId);
  }, [products, partForm.productId]);

  const isValid =
    Boolean(selectedProduct) &&
    Number.isInteger(Number(partForm.quantity)) &&
    Number(partForm.quantity) > 0;

  // ==========================================================
  // FORM
  // ==========================================================

  const handleField = (event) => {
    const { name, value } = event.target;

    setPartForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setPartForm(emptyPart());
  };

  // ==========================================================
  // ADD PART
  // ==========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isValid || submitting || !selectedProduct) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        productId: partForm.productId,
        name: selectedProduct.name || "",
        imageUrl: selectedProduct.imageUrl || "",
        quantity: Number(partForm.quantity),
        comment: partForm.comment.trim(),
      };

      const createdPart = await serviceAddScaleParts(jobId, payload);

      /*
       * Keep enough local data to render immediately
       * even if the API only returns a partial record.
       */
      const displayPart = {
        ...payload,
        ...createdPart,
      };

      setDisplayParts((current) => [...current, displayPart]);

      resetForm();

      toast.success("Part added successfully.");

      onSaved?.(displayPart);
    } catch (error) {
      console.error("Error adding scale part:", error);

      toast.error(error?.message || "Failed to add part.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalSlideOver
      open={open}
      title="Job Parts"
      onClose={() => {
        if (!submitting) {
          onClose?.();
        }
      }}
      footer={
        <button
          type="button"
          className="job-parts-btn primary"
          disabled={submitting}
          onClick={onClose}
        >
          Done
        </button>
      }
    >
      <div className="job-parts-drawer">
        {/* ========== ADD PART ========== */}
        <section className="job-parts-section">
          <div className="job-parts-section-header">
            <div className="job-parts-section-heading">
              <div className="job-parts-section-icon">
                <BsPlusLg />
              </div>

              <div>
                <h3>Add Part</h3>

                <p>
                  Select an inventory product and quantity to allocate to this
                  job.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="job-parts-reset"
              disabled={submitting}
              onClick={resetForm}
            >
              <BsArrowCounterclockwise />
              Clear
            </button>
          </div>

          <form className="job-parts-form" onSubmit={handleSubmit}>
            <div className="job-parts-form-grid">
              {/* dropdown PRODUCT */}
              <div className="job-parts-field">
                <label htmlFor="job-part-product">
                  Product <span>*</span>
                </label>

                <select
                  id="job-part-product"
                  name="productId"
                  value={partForm.productId}
                  disabled={loadingProducts || submitting}
                  onChange={handleField}
                >
                  <option value="">
                    {loadingProducts
                      ? "Loading products..."
                      : "Select product..."}
                  </option>

                  {products.map((product) => {
                    const id = getId(product);

                    return (
                      <option key={id} value={id}>
                        {product.mpn ? `${product.mpn} — ` : ""}
                        {product.name || "Unnamed Product"}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* QUANTITY */}
              <div className="job-parts-field">
                <label htmlFor="job-part-quantity">
                  Quantity <span>*</span>
                </label>

                <input
                  id="job-part-quantity"
                  type="number"
                  name="quantity"
                  min="1"
                  step="1"
                  value={partForm.quantity}
                  disabled={submitting}
                  onChange={handleField}
                />
              </div>
            </div>

            {/* SELECTED PRODUCT */}
            {selectedProduct && <SelectedProduct product={selectedProduct} />}

            <div className="job-parts-form-grid">
              {/* COMMENT */}
              <div className="job-parts-field job-parts-comment">
                <label htmlFor="job-part-comment">Comment</label>

                <textarea
                  id="job-part-comment"
                  rows="3"
                  name="comment"
                  value={partForm.comment}
                  disabled={submitting}
                  placeholder="Optional note about this part..."
                  onChange={handleField}
                />
              </div>

              <div className="job-parts-add-action">
                <button
                  type="submit"
                  className="job-parts-btn primary"
                  disabled={!isValid || submitting}
                >
                  <BsPlusLg />

                  {submitting ? "Adding Part..." : "Add Part"}
                </button>
              </div>
            </div>
          </form>
        </section>

        {/* ========== PARTS ALREADY ADDED ========== */}
        <section className="job-parts-section">
          <div className="job-parts-section-header">
            <div className="job-parts-section-heading">
              <div className="job-parts-section-icon">
                <BsBoxSeam />
              </div>

              <div>
                <h3>Parts Added</h3>

                <p>Products currently allocated to this scale job.</p>
              </div>
            </div>

            <span className="job-parts-count">{displayParts.length}</span>
          </div>

          {displayParts.length === 0 ? (
            <div className="job-parts-empty">
              <div className="job-parts-empty-icon">
                <BsBoxSeam />
              </div>

              <strong>No parts added</strong>

              <span>
                Select a product below to add the first part to this job.
              </span>
            </div>
          ) : (
            <div className="job-parts-table-scroll">
              <table className="job-parts-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Status</th>
                    <th>Comment</th>
                  </tr>
                </thead>

                <tbody>
                  {displayParts.map((part, index) => {
                    const status = part.action || part.status || "required";

                    const imageUrl = getImageUrl(part.imageUrl);

                    return (
                      <tr key={getId(part) || `${part.productId}-${index}`}>
                        <td>
                          <div className="job-parts-list-product">
                            <div className="job-parts-list-image-wrap">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={part.name || "Product"}
                                  className="job-parts-list-image"
                                />
                              ) : (
                                <BsBoxSeam />
                              )}
                            </div>

                            <div className="job-parts-list-details">
                              <strong>
                                {part.name ||
                                  part.productId ||
                                  "Unknown product"}
                              </strong>

                              {part.productId && (
                                <small>{part.productId}</small>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="job-parts-quantity">
                            {part.quantity ?? 0}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`job-parts-status status-${getStatusClass(
                              status,
                            )}`}
                          >
                            {status}
                          </span>
                        </td>

                        <td className="job-parts-comment-cell">
                          {part.comment || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </ModalSlideOver>
  );
};

// ============================================================
// SELECTED PRODUCT
// ============================================================

const SelectedProduct = ({ product }) => {
  const imageUrl = getImageUrl(product.imageUrl);

  return (
    <div className="job-parts-product-preview">
      <div className="job-parts-product-image-wrap">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name || "Product"}
            className="job-parts-product-image"
          />
        ) : (
          <BsBoxSeam />
        )}
      </div>

      <div className="job-parts-product-info">
        <strong>{product.name || "Unnamed Product"}</strong>

        <div className="job-parts-product-meta">
          <ProductMeta label="MPN" value={product.mpn} />

          <ProductMeta label="Type" value={product.type} />

          <ProductMeta label="Shelf" value={product.shelfAddress} />

          <ProductMeta label="Stock" value={product.quantity ?? "—"} />
        </div>
      </div>
    </div>
  );
};

const ProductMeta = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return (
    <span>
      <strong>{label}:</strong> {value}
    </span>
  );
};

export default JobSheetDrawerParts;
